/**
 * 16 MATCHA — Tea Tools Collection
 * jhc-tools-collection.js
 *
 * - Same-page category filtering
 * - Native Shopify collection sorting
 * - Lightweight IntersectionObserver reveals
 * - No scroll listeners
 * - No dependencies
 * - Reduced-motion friendly
 * - Shopify Theme Editor compatible
 */

(() => {
  'use strict';

  const instances = new WeakMap();

  // ---------------------------------------------------------------------
  // REDUCED MOTION
  // ---------------------------------------------------------------------

  function prefersReducedMotion() {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  }

  // ---------------------------------------------------------------------
  // CATEGORY FILTERS
  // ---------------------------------------------------------------------

  function initFilters(root) {
    const buttons = Array.from(
      root.querySelectorAll('[data-tools-filter]')
    );

    const products = Array.from(
      root.querySelectorAll('[data-tools-product]')
    );

    if (!buttons.length || !products.length) {
      return;
    }

    buttons.forEach((button) => {
      if (button.dataset.filterInitialized === 'true') {
        return;
      }

      button.dataset.filterInitialized = 'true';

      button.addEventListener('click', () => {
        const filter = button.dataset.toolsFilter;

        if (!filter) return;

        // Active filter state
        buttons.forEach((item) => {
          const isActive = item === button;

          item.classList.toggle(
            'is-active',
            isActive
          );

          item.setAttribute(
            'aria-pressed',
            String(isActive)
          );
        });

        // Show / hide products
        products.forEach((product) => {
          const tags = (
            product.dataset.toolsTags || ''
          )
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);

          const visible =
            filter === 'all' ||
            tags.includes(filter);

          product.hidden = !visible;
        });
      });
    });
  }

  // ---------------------------------------------------------------------
  // SORTING
  // ---------------------------------------------------------------------

  function initSorting(root) {
    const select = root.querySelector(
      '[data-tools-sort]'
    );

    if (
      !select ||
      select.dataset.sortInitialized === 'true'
    ) {
      return;
    }

    select.dataset.sortInitialized = 'true';

    select.addEventListener('change', () => {
      const url = new URL(
        window.location.href
      );

      url.searchParams.set(
        'sort_by',
        select.value
      );

      /*
       * Reset pagination when sorting changes.
       */
      url.searchParams.delete('page');

      window.location.assign(
        url.toString()
      );
    });
  }

  // ---------------------------------------------------------------------
  // REVEAL ANIMATIONS
  // ---------------------------------------------------------------------

  function initReveals(root) {
    const elements = Array.from(
      root.querySelectorAll(
        '[data-tools-reveal]'
      )
    );

    if (!elements.length) {
      return null;
    }

    if (
      prefersReducedMotion() ||
      !('IntersectionObserver' in window)
    ) {
      elements.forEach((element) => {
        element.classList.remove(
          'jhc-reveal-armed'
        );

        element.classList.add(
          'is-visible'
        );
      });

      return null;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            entry.target.classList.add(
              'is-visible'
            );

            observer.unobserve(
              entry.target
            );
          });
        },
        {
          threshold: 0.1,
          rootMargin:
            '0px 0px -5% 0px',
        }
      );

    elements.forEach((element) => {
      if (
        element.classList.contains(
          'is-visible'
        )
      ) {
        return;
      }

      element.classList.add(
        'jhc-reveal-armed'
      );

      observer.observe(
        element
      );
    });

    return observer;
  }

  // ---------------------------------------------------------------------
  // SECTION INIT
  // ---------------------------------------------------------------------

  function initSection(root) {
    if (
      !root ||
      instances.has(root)
    ) {
      return;
    }

    initFilters(root);
    initSorting(root);

    const observer =
      initReveals(root);

    instances.set(root, {
      observer,
    });
  }

  // ---------------------------------------------------------------------
  // SECTION DESTROY
  // ---------------------------------------------------------------------

  function destroySection(root) {
    const instance =
      instances.get(root);

    if (!instance) {
      return;
    }

    instance.observer?.disconnect();

    instances.delete(root);
  }

  // ---------------------------------------------------------------------
  // FIND SECTION ROOT
  // ---------------------------------------------------------------------

  function getSectionRoot(target) {
    if (
      target?.matches?.(
        '[data-tools-collection]'
      )
    ) {
      return target;
    }

    return target?.querySelector?.(
      '[data-tools-collection]'
    );
  }

  // ---------------------------------------------------------------------
  // INIT ALL
  // ---------------------------------------------------------------------

  function initAll() {
    document
      .querySelectorAll(
        '[data-tools-collection]'
      )
      .forEach(initSection);
  }

  // ---------------------------------------------------------------------
  // INITIAL PAGE LOAD
  // ---------------------------------------------------------------------

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initAll,
      {
        once: true,
      }
    );
  } else {
    initAll();
  }

  // ---------------------------------------------------------------------
  // SHOPIFY THEME EDITOR — SECTION LOAD
  // ---------------------------------------------------------------------

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const root =
        getSectionRoot(
          event.target
        );

      if (root) {
        initSection(root);
      }
    }
  );

  // ---------------------------------------------------------------------
  // SHOPIFY THEME EDITOR — SECTION UNLOAD
  // ---------------------------------------------------------------------

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        getSectionRoot(
          event.target
        );

      if (root) {
        destroySection(root);
      }
    }
  );

  // ---------------------------------------------------------------------
  // SHOPIFY THEME EDITOR — BLOCK SELECT
  // ---------------------------------------------------------------------

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const reveal =
        event.target?.closest?.(
          '[data-tools-reveal]'
        );

      if (!reveal) {
        return;
      }

      reveal.classList.remove(
        'jhc-reveal-armed'
      );

      reveal.classList.add(
        'is-visible'
      );
    }
  );
})();